import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MailIcon from '@mui/icons-material/Mail';
import FolderIcon from '@mui/icons-material/Folder';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PublicIcon from '@mui/icons-material/Public';
import { tokens } from '../../global/theme/tokens';
import { PERMISSION_LEVELS } from './useAiAgentPermissions';

// icon names come from the backend's AgentScopeCatalog
const GROUP_ICONS = {
  account_tree: AccountTreeIcon,
  group: GroupIcon,
  receipt_long: ReceiptLongIcon,
  mail: MailIcon,
  inventory_2: Inventory2Icon,
  folder: FolderIcon,
  schedule: ScheduleIcon,
  account_balance: AccountBalanceIcon,
  public: PublicIcon,
};

/**
 * Section of the chat options menu: one three-way toggle (none / read /
 * write) per feature area the user may delegate to the AI.
 */
export const AiChatPermissionsSection = ({ permissions, canEdit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { groups, loading, error, setLevel } = permissions;

  return (
    <>
      <Box className="flex flex-col gap-1 px-3 pt-3 pb-2">
        <Box className="flex items-center gap-1.5">
          <ShieldOutlinedIcon sx={{ fontSize: 18, color: colors.info }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t('AI permissions')}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.4 }}>
          {t(
            'What the AI may access on your behalf. Nothing is granted by default, and the AI can never do more than you. Applies to all your chats in this browser until you log out.',
          )}
        </Typography>
      </Box>

      <Box className="flex flex-col px-2 pb-2">
        {groups === null && (
          <Box className="flex justify-center py-4">
            <CircularProgress size={18} />
          </Box>
        )}
        {groups?.length === 0 && (
          <Typography variant="body2" sx={{ px: 1, py: 2, opacity: 0.5 }}>
            {t('You have no permissions that could be delegated to the AI.')}
          </Typography>
        )}
        {groups?.map((group) => (
          <PermissionRow
            key={group.id}
            group={group}
            disabled={!canEdit || loading}
            onChange={(level) => setLevel(group.id, level).catch(() => {})}
          />
        ))}
        {error && (
          <Typography
            variant="caption"
            sx={{ px: 1, pt: 1, color: colors.error }}
          >
            {t('Permissions could not be loaded.')}
          </Typography>
        )}
        {!canEdit && groups?.length > 0 && (
          <Typography variant="caption" sx={{ px: 1, pt: 1, opacity: 0.5 }}>
            {t('You only have read access to AI chats and cannot change this.')}
          </Typography>
        )}
      </Box>
    </>
  );
};

const LEVEL_LABELS = { none: 'None', read: 'Read', write: 'Write' };

const PermissionRow = ({ group, disabled, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const Icon = GROUP_ICONS[group.icon] || ShieldOutlinedIcon;
  const maxLevel = PERMISSION_LEVELS.indexOf(group.available);

  return (
    <Box
      className="flex items-center gap-2 px-1.5 py-1 rounded-lg"
      sx={{ '&:hover': { backgroundColor: colors.bgInput } }}
    >
      <Icon
        sx={{
          fontSize: 18,
          flexShrink: 0,
          color: group.granted !== 'none' ? colors.info : colors.text,
          opacity: group.granted !== 'none' ? 1 : 0.5,
        }}
      />
      <Typography variant="body2" noWrap className="flex-1 min-w-0">
        {t(group.name)}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={group.granted}
        onChange={(e, level) =>
          level && level !== group.granted && onChange(level)
        }
        aria-label={t(group.name)}
        sx={{
          flexShrink: 0,
          '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: '0.7rem' },
        }}
      >
        {PERMISSION_LEVELS.map((level, index) => {
          const unavailable = index > maxLevel;
          const button = (
            <ToggleButton
              key={level}
              value={level}
              disabled={disabled || unavailable}
              sx={
                level === 'write' && group.granted === 'write'
                  ? { '&.Mui-selected': { color: colors.warning } }
                  : undefined
              }
            >
              {t(LEVEL_LABELS[level])}
            </ToggleButton>
          );
          return unavailable ? (
            // a disabled button swallows pointer events, so wrap it for the tooltip
            <Tooltip
              key={level}
              title={t('Not available for this area')}
              disableInteractive
            >
              <span className="flex">{button}</span>
            </Tooltip>
          ) : (
            button
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};
